import {
  APPROVAL_CREATED,
  APPROVAL_RESOLVED,
  ATLAS_AI_CANCELLED,
  ATLAS_AI_COMPLETE,
  ATLAS_AI_FAILED,
  ATLAS_AI_PLAN_STATUS,
  ATLAS_AI_SUBAGENT_STATUS,
  ATLAS_AI_TYPING_STOP,
  CHANNEL_ARC_CANCELLED,
} from '@common/constants/webSockets'
import {
  ApprovalResolvingStatus,
  IApprovalSummary,
  IArcPlanStatus,
  IBulkApprovalResult,
  IPendingApprovalsState,
  ISubagentStatus,
  isActiveSubagentStatus,
} from '@common/interfaces/atlasai'
import { combineReducers } from 'redux'
import { IAction, IActionWithParams } from '../apiMiddleware'
// Cross-domain: a conversation with Arc is a channel, so the fetch that hydrates
// the plan panel is a channels action. No cycle — channels/actions imports
// nothing from this domain.
import { ARC_PLAN_REQUEST, ARC_PLAN_SUCCESS } from '../channels/actions'
import * as actions from './actions'
import automations from './automations/reducer'
import { IBulkApprovalAbort } from './bulkApprovals'
import docs from './docs/reducer'
import memories from './memories/reducer'
import prompts from './prompts/reducer'
import workflows from './workflows/reducer'

/**
 * What is left of the Arc chat store.
 *
 * A conversation with Arc is a channel now (see `redux/channels`), which owns its
 * messages, sessions, drafts, unread counts, typing and model choice. What stays
 * here is what is about the AGENT rather than about the conversation carrying it:
 * the live plan and subagent panels, the approval gate, the recipient picker the
 * Automations page uses, and the notification hand-off that asks the floating
 * bubble to open a conversation. The knowledge sub-stores (automations, docs,
 * memories, prompts, workflows) are their own pages and are untouched by any of
 * that.
 */

// Immutably drop ids from the resolving map — used on every APPROVE/REJECT
// SUCCESS or FAILURE, single or bulk, to clear the in-flight state for those rows.
function clearResolving(
  resolving: IPendingApprovalsState['resolving'],
  ids: string[],
): IPendingApprovalsState['resolving'] {
  const next = { ...resolving }
  ids.forEach((id) => delete next[id])
  return next
}

// Flag rows as in-flight so their card shows the spinner and refuses a second click.
function markResolving(
  resolving: IPendingApprovalsState['resolving'],
  ids: string[],
  status: ApprovalResolvingStatus,
): IPendingApprovalsState['resolving'] {
  const next = { ...resolving }
  ids.forEach((id) => {
    next[id] = status
  })
  return next
}

// Retire resolved rows: off the list, and out of the in-flight map with them.
function withoutApprovals(state: IPendingApprovalsState, ids: string[]): IPendingApprovalsState {
  const resolved = new Set(ids)
  return {
    pending: state.pending.filter((approval) => !resolved.has(approval.id)),
    resolving: clearResolving(state.resolving, ids),
  }
}

// Approve/Reject refuse a card that has already left Pending with "Approval is
// already <status>…" — a card in that state can never land, so it has to go.
const isAlreadyResolvedError = (error: IAction['error']): boolean =>
  /\balready\b/i.test(error?.message ?? '')

/**
 * One session's plan, plus the bookkeeping that decides whether a hydration
 * response is still allowed to overwrite it.
 *
 * Held as a single record rather than parallel maps so the plan and the guard
 * protecting it cannot drift apart.
 */
export interface IArcPlanEntry {
  /** The live plan, or null once something authoritative said there is none. */
  plan: IArcPlanStatus | null
  /** Bumped by every live event for this session. */
  generation: number
  /** The newest hydration request for this session, and the generation it began at. */
  request: { id: string; generation: number } | null
}

export interface IArcPlanSliceState {
  [sessionId: string]: IArcPlanEntry
}

const emptyPlanEntry: IArcPlanEntry = { plan: null, generation: 0, request: null }

/** What a hydration request carries so its response can be recognised on arrival. */
interface IArcPlanRequestParams {
  sessionId?: string
  requestId?: string
}

/**
 * Record what a live event said, and move the session on a generation — which is
 * what makes every response already in flight for it stale.
 */
const bumpGeneration = (
  entry: IArcPlanEntry | undefined,
  plan: IArcPlanStatus | null,
): IArcPlanEntry => {
  const current = entry ?? emptyPlanEntry
  return { ...current, plan, generation: current.generation + 1 }
}

const initialPendingApprovals: IPendingApprovalsState = {
  pending: [],
  resolving: {},
}

export default combineReducers({
  // Per-session counter of in-flight requests. Boolean was insufficient: a
  // user can queue Q2 while Q1 is still running, and the Q1 completion event
  // shouldn't clear the spinner while Q2 is mid-flight.
  messagesChannelRequest: (
    state: actions.IMessagesChannelRequest | null = null,
    action: IAction,
  ): actions.IMessagesChannelRequest | null => {
    switch (action.type) {
      case actions.REQUEST_OPEN_MESSAGES_CHANNEL:
        return action.payload as actions.IMessagesChannelRequest
      case actions.CLEAR_MESSAGES_CHANNEL_REQUEST:
        return null
      default:
        return state
    }
  },

  // A preview card's Message; Messages consumes and clears it.
  messagesDmRequest: (
    state: actions.IMessagesDmRequest | null = null,
    action: IAction,
  ): actions.IMessagesDmRequest | null => {
    switch (action.type) {
      case actions.REQUEST_START_MESSAGES_DM:
        return action.payload as actions.IMessagesDmRequest
      case actions.CLEAR_MESSAGES_DM_REQUEST:
        return null
      default:
        return state
    }
  },

  pendingApprovals: (
    state: IPendingApprovalsState = initialPendingApprovals,
    action: IAction,
  ): IPendingApprovalsState => {
    switch (action.type) {
      /**
       * Scoped fetches merge; only an unscoped one replaces.
       *
       * There is more than one place that lists approvals now — a conversation for
       * its own Arc session, a message thread for the session bound to it, a Task
       * detail panel for its task — and several can be mounted at once (the task
       * drawer opens over a page while /messages holds its own). Replacing the
       * whole list on every SUCCESS meant whichever refetched last erased the
       * others' cards while its own request was still the only thing in state.
       *
       * The merge key is whichever scope was ASKED for, not `sessionId` alone: a
       * task-scoped fetch carries no sessionId, so keying on that would send it
       * down the unscoped branch and wipe every card the chat surfaces had.
       * Rows matching the fetched scope are replaced wholesale by what came back
       * — including ones another surface put there, which is correct, since the
       * response is authoritative for that scope — and everything else is left
       * alone.
       *
       * `resolving` is likewise preserved on a scoped fetch — clearing it would
       * drop the in-flight spinner on a card another mount is mid-approve on.
       */
      case actions.LIST_APPROVALS_SUCCESS: {
        const data = action.data as { approvals?: IApprovalSummary[] } | undefined
        const fetched: IApprovalSummary[] = data?.approvals ?? []
        const { sessionId, taskId } =
          (action.params as { sessionId?: string; taskId?: string } | undefined) ?? {}
        if (!sessionId && !taskId) return { pending: fetched, resolving: {} }
        const isInFetchedScope = (approval: IApprovalSummary) =>
          (!sessionId || approval.sessionId === sessionId) &&
          (!taskId || approval.taskId === taskId)
        return {
          pending: [...fetched, ...state.pending.filter((p) => !isInFetchedScope(p))],
          resolving: state.resolving,
        }
      }
      // Pushed by the server when a card is raised mid-turn — prepend (newest
      // first, matching List's DESC order) and dedupe so a later refetch can't
      // double-add it.
      case APPROVAL_CREATED: {
        const data = action.data as { approval?: IApprovalSummary } | undefined
        const approval: IApprovalSummary | undefined = data?.approval
        if (!approval?.id || state.pending.some((p) => p.id === approval.id)) return state
        return { ...state, pending: [approval, ...state.pending] }
      }
      // Pushed when an approval leaves Pending — executed, rejected or failed — so
      // the owner's other tabs and group-session viewers drop the card.
      case APPROVAL_RESOLVED: {
        const data = action.data as { approvalId?: string } | undefined
        const id: string | undefined = data?.approvalId
        if (!id) return state
        return withoutApprovals(state, [id])
      }
      case actions.APPROVE_APPROVAL_REQUEST: {
        const params = action.params as { approvalId: string }
        return {
          ...state,
          resolving: markResolving(
            state.resolving,
            [params.approvalId],
            ApprovalResolvingStatus.Approving,
          ),
        }
      }
      case actions.REJECT_APPROVAL_REQUEST: {
        const params = action.params as { approvalId: string }
        return {
          ...state,
          resolving: markResolving(
            state.resolving,
            [params.approvalId],
            ApprovalResolvingStatus.Rejecting,
          ),
        }
      }
      case actions.APPROVE_APPROVAL_SUCCESS:
      case actions.REJECT_APPROVAL_SUCCESS: {
        const id: string | undefined =
          (action.data as { approvalId?: string } | undefined)?.approvalId ??
          (action.params as { approvalId?: string } | undefined)?.approvalId
        if (!id) return state
        return withoutApprovals(state, [id])
      }
      // A failed approve is marked Failed server-side, so neither button can ever
      // work on it again. A reject that failed for any other reason (a dropped
      // connection) keeps its card so the user can retry.
      case actions.APPROVE_APPROVAL_FAILURE:
      case actions.REJECT_APPROVAL_FAILURE: {
        const id: string | undefined = (action.params as { approvalId?: string } | undefined)
          ?.approvalId
        if (!id) return state
        if (
          action.type === actions.APPROVE_APPROVAL_FAILURE ||
          isAlreadyResolvedError(action.error)
        ) {
          return withoutApprovals(state, [id])
        }
        return {
          ...state,
          resolving: clearResolving(state.resolving, [id]),
        }
      }
      // ── Bulk approve/reject ──────────────────────────────────────────────
      // Every selected card goes in-flight at once; the batch itself is just the
      // single-item requests run in bounded windows (see `bulkApprovals`).
      case actions.APPROVE_APPROVALS_REQUEST:
      case actions.REJECT_APPROVALS_REQUEST: {
        const { approvalIds = [] } = (action.params as { approvalIds?: string[] } | undefined) ?? {}
        return {
          ...state,
          resolving: markResolving(
            state.resolving,
            approvalIds,
            action.type === actions.APPROVE_APPROVALS_REQUEST
              ? ApprovalResolvingStatus.Approving
              : ApprovalResolvingStatus.Rejecting,
          ),
        }
      }
      // Only what landed leaves the list. A card that failed stays put, cleared
      // of its spinner, so the user can retry just that one — unless the server
      // retired it as Failed, in which case APPROVAL_RESOLVED drops it and the
      // panel keeps the row on screen for its reason.
      //
      // FAILURE is the batch aborting on an error apiMiddleware handles globally
      // (an expired session, someone else's card). Anything it had already
      // resolved really did happen, so it rides along on `error.bulkResult` and is
      // retired the same way — see `resolveApprovalsInBatches`.
      case actions.APPROVE_APPROVALS_SUCCESS:
      case actions.REJECT_APPROVALS_SUCCESS:
      case actions.APPROVE_APPROVALS_FAILURE:
      case actions.REJECT_APPROVALS_FAILURE: {
        const { approvalIds = [] } = (action.params as { approvalIds?: string[] } | undefined) ?? {}
        const result =
          (action.data as IBulkApprovalResult | undefined) ??
          (action.error as Partial<IBulkApprovalAbort> | undefined)?.bulkResult
        return {
          ...withoutApprovals(state, result?.succeededIds ?? []),
          resolving: clearResolving(state.resolving, approvalIds),
        }
      }
      default:
        return state
    }
  },

  inviteableUsers: (
    state: {
      isLoading: boolean
      data: Array<{ id: string; name: string; email: string; avatar: string | null }>
    } = { isLoading: false, data: [] },
    action: IAction,
  ): {
    isLoading: boolean
    data: Array<{ id: string; name: string; email: string; avatar: string | null }>
  } => {
    switch (action.type) {
      case actions.LIST_INVITEABLE_USERS_REQUEST:
        return { ...state, isLoading: true }
      case actions.LIST_INVITEABLE_USERS_SUCCESS: {
        const data = action.data as
          | {
              users?: Array<{ id: string; name: string; email: string; avatar: string | null }>
            }
          | undefined
        return { isLoading: false, data: data?.users ?? [] }
      }
      case actions.LIST_INVITEABLE_USERS_FAILURE:
        return { ...state, isLoading: false }
      default:
        return state
    }
  },

  subagents: (
    state: { [sessionId: string]: ISubagentStatus[] } = {},
    action: IAction,
  ): { [sessionId: string]: ISubagentStatus[] } => {
    // Once every subagent has finished, the parent is woken to synthesize the
    // answer — but that wake turn is server-driven and never bumps `isLoading`,
    // so without this the panel would vanish and the user would stare at
    // nothing until the answer pops in. Instead we keep the (all-terminal)
    // panel mounted so it can show an "analyzing results…" step, and only drop
    // it when that synthesis turn actually ends. Guard on all-terminal so a
    // normal turn completing (e.g. the interim answer right after a spawn,
    // while subagents are still running) does NOT clear a live running panel.
    const clearWhenAnalyzing = (sessionId: string | undefined) => {
      if (!sessionId) return state
      const items = state[sessionId]
      if (!items) return state
      if (items.some((s) => isActiveSubagentStatus(s.status))) return state
      const next = { ...state }
      delete next[sessionId]
      return next
    }

    switch (action.type) {
      case ATLAS_AI_SUBAGENT_STATUS: {
        const data = action.data as
          { sessionId?: string; subagents?: ISubagentStatus[] } | undefined
        const sid = data?.sessionId
        if (!sid) return state
        const items = data?.subagents ?? []
        // Ignore an all-terminal batch that arrives with no panel already up —
        // nothing was ever shown running, so there's no transition to render.
        // Otherwise keep the entry (running OR just-finished/analyzing); the
        // clear happens on the synthesis turn's terminal event below.
        const anyActive = items.some((s) => isActiveSubagentStatus(s.status))
        if (!anyActive && !state[sid]) return state
        return { ...state, [sid]: items }
      }
      case ATLAS_AI_COMPLETE:
      case ATLAS_AI_FAILED:
      case ATLAS_AI_TYPING_STOP:
        // A turn ending clears the panel only if we're in the analyzing phase
        // (all subagents terminal). Subagents are detached, so an interim
        // answer or a failed spawning turn must NOT wipe a still-running panel.
        return clearWhenAnalyzing((action.data as { sessionId?: string } | undefined)?.sessionId)
      case ATLAS_AI_CANCELLED: {
        // A parent Stop cancels the subagents too (shared cancelSubagentRuns),
        // so tear the panel down unconditionally rather than waiting on the
        // trailing all-cancelled status push.
        const sid = (action.data as { sessionId?: string } | undefined)?.sessionId
        if (!sid || !state[sid]) return state
        const next = { ...state }
        delete next[sid]
        return next
      }
      case actions.CLEAR_SUBAGENT_PANEL: {
        // Watchdog force-clear: the analyzing panel outlived its synthesis turn
        // without a clearing terminal event. Drop it unconditionally (unlike
        // clearWhenAnalyzing, which bails while any subagent still reads active).
        const sid = (action.payload as { sessionId?: string } | undefined)?.sessionId
        if (!sid || !state[sid]) return state
        const next = { ...state }
        delete next[sid]
        return next
      }
      default:
        return state
    }
  },

  /**
   * The session's live multi-step plan.
   *
   * Two things write here and they are not equally fresh. `ATLAS_AI_PLAN_STATUS`
   * and the cancel events are live pushes — true as of the moment they arrive, and
   * authoritative in both directions: the server sends `plan: null` when a plan
   * goes terminal, so the clear is stated rather than inferred, and a plan that
   * merely halts (AWAITING_INPUT / PAUSED) is still sent, because seeing where it
   * stopped is the entire point of pausing. `ARC_PLAN_SUCCESS` is different: an
   * HTTP response that was true when the server answered it, possibly several
   * hundred milliseconds ago.
   *
   * Guarding that with a flag does not work, and this is the third attempt at it.
   * Refusing to install over any recorded value fixed the race but left the fetch
   * unable to reconcile a missed push; a per-session boolean fixed that but could
   * not describe more than one request — leaving a conversation and coming back
   * re-dispatches while the first is still in flight, and whichever response landed
   * second installed its stale body over the live state.
   *
   * So each response carries enough identity to answer the only question that
   * matters: *was I already obsolete when I landed?* It may install only if it
   * belongs to the newest request for the session AND no live event has occurred
   * since that request began. The first condition discards an overtaken or
   * out-of-order response; the second discards one whose request predates a push.
   * Neither is a heuristic, which is what the previous two attempts were.
   */
  plan: (state: IArcPlanSliceState = {}, action: IAction): IArcPlanSliceState => {
    switch (action.type) {
      case ARC_PLAN_REQUEST: {
        const params = (action as IActionWithParams<IArcPlanRequestParams>).params
        const sid = params?.sessionId
        if (!sid || !params?.requestId) return state
        const entry = state[sid] ?? emptyPlanEntry
        // Overwritten rather than accumulated: only the newest request is ever
        // eligible, so there is no pending-request map to keep or leak.
        return {
          ...state,
          [sid]: { ...entry, request: { id: params.requestId, generation: entry.generation } },
        }
      }
      case ATLAS_AI_PLAN_STATUS: {
        const data = action.data as { sessionId?: string; plan?: IArcPlanStatus | null } | undefined
        const sid = data?.sessionId
        if (!sid) return state
        return { ...state, [sid]: bumpGeneration(state[sid], data?.plan ?? null) }
      }
      /**
       * Hydrate on open.
       *
       * `ATLAS_AI_PLAN_STATUS` only fires when something CHANGES, and a plan parked
       * in AWAITING_INPUT changes nothing until the user answers it — so a reload,
       * a second tab, or a teammate opening the conversation showed no panel at all
       * over a plan that was very much alive. Messages fetches it per session
       * (channels/GetArcPlan) and it lands here, because this is where the panel
       * reads from and where the websocket pushes to; two stores for one panel
       * would be two things to keep agreeing.
       */
      case ARC_PLAN_SUCCESS: {
        const data = action.data as { sessionId?: string; plan?: IArcPlanStatus | null } | undefined
        const requestId = (action as IActionWithParams<IArcPlanRequestParams>).params?.requestId
        const sid = data?.sessionId
        if (!sid) return state

        const entry = state[sid]
        const isNewest = !!requestId && entry?.request?.id === requestId
        const isCurrent = entry?.request?.generation === entry?.generation
        if (!entry || !isNewest || !isCurrent) return state

        return { ...state, [sid]: { ...entry, plan: data?.plan ?? null } }
      }
      // A Stop abandons the session's plan server-side, which emits its own cleared
      // status — but `emitPlanStatus` targets one socket, so in a shared
      // conversation only the person who pressed it would see the panel go. The
      // channel event is membership-scoped, so everyone watching clears together.
      // Either way this is the optimistic half: drop the panel now rather than
      // waiting on a trailing push.
      case ATLAS_AI_CANCELLED:
      case CHANNEL_ARC_CANCELLED: {
        const sid = (action.data as { sessionId?: string } | undefined)?.sessionId
        if (!sid) return state
        return { ...state, [sid]: bumpGeneration(state[sid], null) }
      }
      default:
        return state
    }
  },

  // Sub-domains under the AtlasAI redux umbrella. Co-located here so all
  // AtlasAI-related state lives in one place rather than spread across
  // separate top-level redux folders.
  automations,
  docs,
  memories,
  prompts,
  workflows,
})
